/**
 * TicketActionsMenu Component
 *
 * Dropdown menu with quick actions for queue tickets.
 * Actions shown are context-sensitive based on ticket status:
 * - waiting: Llamar (call next)
 * - notified: Marcar Llegada, Quick Action "Cortando"
 * - arrived: Iniciar Servicio
 * - in_service/finishing: Completar, Añadir Tiempo
 * - any active status: Cancelar
 *
 * Features:
 * - Dropdown with 3-dots icon
 * - Keyboard accessible
 * - Confirmation for critical actions (Cancel)
 * - Disabled actions based on status
 * - Icons for each action
 */

import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { MoreVertical, Phone, CheckCircle2, XCircle, UserCheck, Clock, Play, Scissors } from 'lucide-react';
import type { QueueTicket } from '../../types';

interface TicketActionsMenuProps {
  ticket: QueueTicket;
  onCallClient: () => void;
  onMarkArrival: () => void;
  onStartService: () => void;
  onComplete: () => void;
  onCancel: () => void;
  onAddTime?: () => void; // Optional: Add time to ticket
  disabled?: boolean;
}

export function TicketActionsMenu({
  ticket,
  onCallClient,
  onMarkArrival,
  onStartService,
  onComplete,
  onCancel,
  onAddTime,
  disabled = false,
}: TicketActionsMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, right: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);

  const portalRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        portalRef.current && !portalRef.current.contains(target) &&
        buttonRef.current && !buttonRef.current.contains(target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Close menu on scroll (position becomes stale)
  useEffect(() => {
    if (!isOpen) return;

    const handleScroll = () => setIsOpen(false);
    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [isOpen]);

  // Determine which actions to show based on status
  const canCall = ticket.status === 'waiting';
  const canMarkArrival = ticket.status === 'notified';
  const canStartService = ticket.status === 'arrived';
  const canStartCutting = ticket.status === 'notified'; // Quick action: mark arrival + start service
  const canComplete = ticket.status === 'in_service' || ticket.status === 'finishing';
  const canCancel =
    ticket.status === 'waiting' ||
    ticket.status === 'notified' ||
    ticket.status === 'arrived' ||
    ticket.status === 'in_service' ||
    ticket.status === 'finishing';

  const handleAction = (action: () => void) => {
    setIsOpen(false);
    action();
  };

  const handleCancel = () => {
    setIsOpen(false);
    onCancel();
  };

  const handleToggleMenu = () => {
    if (!isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setMenuPosition({
        top: rect.bottom + 4,
        right: window.innerWidth - rect.right,
      });
    }
    setIsOpen(!isOpen);
  };

  return (
    <div className="relative">
      {/* Trigger button */}
      <button
        ref={buttonRef}
        onClick={handleToggleMenu}
        disabled={disabled}
        className="p-2.5 text-ink-subtle hover:text-ink hover:bg-surface-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        aria-label="Acciones del ticket"
        aria-haspopup="true"
        aria-expanded={isOpen}
      >
        <MoreVertical size={22} />
      </button>

      {/* Dropdown menu - Rendered via Portal to escape transform/overflow parents */}
      {isOpen && createPortal(
        <div
          ref={portalRef}
          className="fixed min-w-[180px] bg-surface rounded-lg shadow-xl border border-hairline z-[9999]"
          style={{ top: `${menuPosition.top}px`, right: `${menuPosition.right}px` }}
        >
          <div className="py-0.5">
            {/* Call Client */}
            {canCall && (
              <button
                onClick={() => handleAction(onCallClient)}
                className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm text-ink hover:bg-surface-2"
              >
                <Phone size={14} className="text-ink-subtle flex-shrink-0" />
                <span>Llamar Cliente</span>
              </button>
            )}

            {/* Quick Action: Start Cutting (when notified) — highlighted */}
            {canStartCutting && (
              <button
                onClick={() => {
                  handleAction(() => {
                    onMarkArrival();
                    setTimeout(() => onStartService(), 500);
                  });
                }}
                className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm font-semibold text-on-accent bg-accent hover:bg-accent-hover"
              >
                <Scissors size={14} className="flex-shrink-0" />
                <span>Cortando</span>
              </button>
            )}

            {/* Mark Arrival */}
            {canMarkArrival && (
              <button
                onClick={() => handleAction(onMarkArrival)}
                className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm text-ink hover:bg-surface-2"
              >
                <UserCheck size={14} className="text-ink-subtle flex-shrink-0" />
                <span>Marcar Llegada</span>
              </button>
            )}

            {/* Start Service */}
            {canStartService && (
              <button
                onClick={() => handleAction(onStartService)}
                className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm text-ink hover:bg-surface-2"
              >
                <Play size={14} className="text-ink-subtle flex-shrink-0" />
                <span>Iniciar Servicio</span>
              </button>
            )}

            {/* Complete Service */}
            {canComplete && (
              <button
                onClick={() => handleAction(onComplete)}
                className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm text-ink hover:bg-surface-2"
              >
                <CheckCircle2 size={14} className="text-ink-subtle flex-shrink-0" />
                <span>Completar Servicio</span>
              </button>
            )}

            {/* Add Time */}
            {onAddTime && canCancel && (
              <button
                onClick={() => handleAction(onAddTime)}
                className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm text-ink hover:bg-surface-2"
              >
                <Clock size={14} className="text-ink-subtle flex-shrink-0" />
                <span>Añadir Tiempo</span>
              </button>
            )}

            {/* Separator */}
            {canCancel && (canCall || canMarkArrival || canStartService || canComplete || onAddTime) && (
              <div className="border-t border-hairline my-0.5" />
            )}

            {/* Cancel Ticket */}
            {canCancel && (
              <button
                onClick={handleCancel}
                className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm text-danger hover:bg-danger/20"
              >
                <XCircle size={14} className="flex-shrink-0" />
                <span>Cancelar Turno</span>
              </button>
            )}

            {/* No actions available */}
            {!canCall && !canMarkArrival && !canStartService && !canComplete && !canCancel && (
              <div className="px-3 py-2 text-center text-xs text-ink-subtle">
                Sin acciones
              </div>
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
